"use client";

import { useState, type FormEvent } from "react";
import Link from "next/link";
import { ArrowRight, Check } from "./icons";

type Status = "idle" | "sending" | "success" | "error";

const inputClass =
  "w-full rounded-xl border border-line bg-bg px-4 py-3 text-[15px] text-ink outline-none transition placeholder:text-muted/70 focus:border-brand focus:ring-4 focus:ring-brand/15";

function Field({
  id,
  label,
  optional,
  ...rest
}: React.InputHTMLAttributes<HTMLInputElement> & {
  id: string;
  label: string;
  optional?: boolean;
}) {
  return (
    <div>
      <label htmlFor={id} className="mb-1.5 block text-sm font-semibold">
        {label}
        {optional && <span className="ml-1 font-normal text-muted">(optional)</span>}
      </label>
      <input id={id} name={id} className={inputClass} {...rest} />
    </div>
  );
}

export function ContactForm() {
  const [status, setStatus] = useState<Status>("idle");

  async function onSubmit(e: FormEvent<HTMLFormElement>) {
    e.preventDefault();
    const form = e.currentTarget;
    const data = Object.fromEntries(new FormData(form).entries());
    setStatus("sending");
    try {
      const res = await fetch("/api/anfrage", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(data),
      });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      setStatus("success");
      form.reset();
    } catch {
      setStatus("error");
    }
  }

  if (status === "success") {
    return (
      <div className="card p-8 text-center text-ink sm:p-10" role="status" aria-live="polite">
        <span className="mx-auto grid size-14 place-items-center rounded-full bg-brand text-white">
          <Check className="size-7" />
        </span>
        <h3 className="mt-5 text-2xl font-bold">Danke, ist angekommen.</h3>
        <p className="mt-2 text-muted">
          Dein Projektleiter meldet sich am nächsten Werktag, um das Beratungsgespräch zu vereinbaren.
        </p>
        <button type="button" onClick={() => setStatus("idle")} className="btn-secondary mt-6">
          Noch eine Anfrage senden
        </button>
      </div>
    );
  }

  return (
    <form onSubmit={onSubmit} className="card relative p-6 text-ink sm:p-8">
      <div className="flex items-start gap-3">
        <span className="font-display text-2xl leading-none text-brand" aria-hidden>
          ✳
        </span>
        <div>
          <h3 className="text-xl font-bold">Kostenlosen Entwurf anfragen.</h3>
          <p className="mt-1 text-sm text-muted">
            Ein paar Angaben, dann meldet sich dein Projektleiter für das 60-Minuten-Gespräch.
          </p>
        </div>
      </div>

      {/* Honeypot: bleibt für Menschen unsichtbar und leer */}
      <div className="absolute -left-[9999px] top-auto h-px w-px overflow-hidden" aria-hidden="true">
        <label htmlFor="referenz">Referenz</label>
        <input id="referenz" name="referenz" type="text" tabIndex={-1} autoComplete="off" />
      </div>

      <div className="mt-6 grid gap-4 sm:grid-cols-2">
        <Field id="vorname" label="Vorname" placeholder="Vorname" autoComplete="given-name" required />
        <Field id="nachname" label="Nachname" placeholder="Nachname" autoComplete="family-name" required />
      </div>
      <div className="mt-4 grid gap-4">
        <Field id="firma" label="Firma" placeholder="Wie heißt dein Unternehmen?" autoComplete="organization" required />
        <div className="grid gap-4 sm:grid-cols-2">
          <Field id="email" label="E-Mail" type="email" placeholder="name@firma.de" autoComplete="email" inputMode="email" required />
          <Field id="telefon" label="Telefon" optional type="tel" placeholder="+49 …" autoComplete="tel" inputMode="tel" />
        </div>
        <Field id="website" label="Aktuelle Website" optional type="url" placeholder="https://www.firma.de" autoComplete="url" inputMode="url" />
      </div>

      <label className="mt-5 flex items-start gap-3 text-sm text-muted">
        <input type="checkbox" name="datenschutz" required className="mt-0.5 size-4 shrink-0 accent-brand" />
        <span>
          Ich bin einverstanden, dass meine Angaben zur Kontaktaufnahme gespeichert werden.{" "}
          <Link href="/datenschutz" className="underline underline-offset-2 hover:text-ink">
            Datenschutzerklärung
          </Link>
        </span>
      </label>

      {status === "error" && (
        <p className="mt-4 rounded-xl bg-red-50 px-4 py-3 text-sm text-red-700" role="alert">
          Das hat leider nicht geklappt. Versuch es bitte noch einmal oder schreib uns direkt per E-Mail.
        </p>
      )}

      <button
        type="submit"
        disabled={status === "sending"}
        className="btn-primary mt-6 w-full disabled:cursor-wait disabled:opacity-70"
      >
        {status === "sending" ? "Wird gesendet …" : "Entwurf anfragen"}
        <ArrowRight className="size-4" />
      </button>
      <p className="mt-3 text-center text-xs text-muted">
        Kostenlos. Unverbindlich. Antwort am nächsten Werktag.
      </p>
    </form>
  );
}
